import React, { memo, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import RegionApi from '../api/RegionApi';
import DistrictApi from '../api/DistrictApi';
import FormationSanitaireApi from '../api/FormationSanitaireApi';
import MembreApi from '../api/MembreApi';
import Membre from '../modeles/Membre';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    background: 'linear-gradient(145deg, #f5f9f7 0%, #edf5ff 100%)',
    borderRadius: '18px',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
  },
  formShell: {
    maxWidth: '980px',
    margin: '0 auto',
  },
  card: {
    padding: theme.spacing(3),
    borderRadius: '18px',
    background: 'rgba(255, 255, 255, 0.92)',
    border: '1px solid rgba(16, 66, 52, 0.08)',
    boxShadow: '0 18px 40px rgba(9, 47, 36, 0.12)',
    backdropFilter: 'blur(1px)',
  },
  eyebrow: {
    margin: 0,
    color: '#0f5132',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontSize: '0.78rem',
    fontWeight: 800,
  },
  title: {
    marginTop: theme.spacing(0.6),
    marginBottom: theme.spacing(0.8),
    fontWeight: 700,
    color: '#1a2f2a',
    fontFamily: 'Poppins, Nunito, sans-serif',
  },
  subtitle: {
    marginTop: 0,
    marginBottom: theme.spacing(2),
    color: '#45625a',
    lineHeight: 1.5,
    fontSize: '0.95rem',
  },
  field: {
    '& .MuiInputBase-root': {
      borderRadius: '12px',
      background: '#ffffff',
    },
  },
  submitRow: {
    marginTop: theme.spacing(1),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  submit: {
    textTransform: 'none',
    fontWeight: 700,
    padding: theme.spacing(1.1, 3),
    borderRadius: '999px',
    background: 'linear-gradient(90deg, #0f766e 0%, #0f9a6d 100%)',
    boxShadow: '0 8px 24px rgba(15, 118, 110, 0.3)',
    '&:hover': {
      background: 'linear-gradient(90deg, #0d665f 0%, #0e8b61 100%)',
    },
  },
  feedback: {
    marginTop: theme.spacing(1),
    fontSize: '0.9rem',
  },
  success: {
    color: '#0f766e',
  },
  error: {
    color: '#b91c1c',
  },
}));

const normalizeList = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data.toArray === 'function') return data.toArray();
  return [];
};

const InscriptionContainer = memo(() => {
  const classes = useStyles();
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [formations, setFormations] = useState([]);
  const [soumissionEnCours, setSoumissionEnCours] = useState(false);
  const [messageSucces, setMessageSucces] = useState('');
  const [messageErreur, setMessageErreur] = useState('');
  const [membre, setMembre] = useState(new Membre());
  const [regionSanitaire, setRegionSanitaire] = useState('');
  const [districtSanitaire, setDistrictSanitaire] = useState('');

  useEffect(() => {
    const loadRegions = async () => {
      try {
        const data = await RegionApi.obtenirToutesLesRegions();
        setRegions(normalizeList(data));
      } catch (error) {
        console.error('Erreur chargement regions:', error);
        setRegions([]);
      }
    };

    loadRegions();
  }, []);

  useEffect(() => {
    const loadDistricts = async () => {
      if (!regionSanitaire) {
        setDistricts([]);
        setFormations([]);
        setDistrictSanitaire('');
        setMembre(prev => prev.setIdStructure('').setStructure(''));
        return;
      }

      try {
        const data = await DistrictApi.obtenirDistrictsRegionCourante(regionSanitaire);
        setDistricts(normalizeList(data));
      } catch (error) {
        console.error('Erreur chargement districts:', error);
        setDistricts([]);
      }
    };

    loadDistricts();
  }, [regionSanitaire]);

  useEffect(() => {
    const loadFormations = async () => {
      if (!districtSanitaire) {
        setFormations([]);
        setMembre(prev => prev.setIdStructure('').setStructure(''));
        return;
      }

      try {
        // Appel corrigé : récupère les formations sanitaires du district sélectionné
        const data = await FormationSanitaireApi.obtenirFormationsSanitaires(districtSanitaire);
        setFormations(normalizeList(data));
      } catch (error) {
        console.error('Erreur chargement formations sanitaires:', error);
        setFormations([]);
      }
    };

    loadFormations();
  }, [districtSanitaire]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'regionSanitaire') {
      setRegionSanitaire(value);
      setDistrictSanitaire('');
      setMembre(prev => prev.setIdStructure('').setStructure(''));
      return;
    }

    if (name === 'districtSanitaire') {
      setDistrictSanitaire(value);
      setMembre(prev => prev.setIdStructure('').setStructure(''));
      return;
    }

    if (name === 'idStructure') {
      const formationSelectionnee = formations.find((formation) => formation.id === value);
      setMembre(prev => prev.setIdStructure(value).setStructure(formationSelectionnee?.nom || ''));
      return;
    }

    if (name === 'nom') {
      setMembre(prev => prev.setNom(value));
      return;
    }

    if (name === 'prenom') {
      setMembre(prev => prev.setPrenom(value));
      return;
    }

    if (name === 'structure') {
      setMembre(prev => prev.setStructure(value));
      return;
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setMessageSucces('');
    setMessageErreur('');

    // Utilise le modèle Membre pour générer le payload conforme
    const payload = membre.setDateInscription(new Date().toISOString()).toDto();

    const soumettre = async () => {
      setSoumissionEnCours(true);
      try {
        await MembreApi.ajouterMembre(payload);
        setMessageSucces('Inscription enregistree avec succes.');
        setMembre(new Membre());
        setRegionSanitaire('');
        setDistrictSanitaire('');
        setDistricts([]);
        setFormations([]);
      } catch (error) {
        setMessageErreur("Impossible d'enregistrer le membre pour le moment.");
      } finally {
        setSoumissionEnCours(false);
      }
    };

    soumettre();
  };

  return (
    <div className={classes.root}>
      <div className={classes.formShell}>
      <Paper className={classes.card} elevation={0}>
        <p className={classes.eyebrow}>Etape 2</p>
        <h2 className={classes.title}>Finaliser votre profil membre</h2>
        <p className={classes.subtitle}>
          Completez ces informations pour finaliser votre inscription commencée a l'etape precedente.
        </p>
        <Divider style={{ marginBottom: '20px' }} />

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2.2}>
            <Grid item xs={12} md={6}>
              <TextField className={classes.field} label="Nom" name="nom" value={membre.nom} onChange={handleChange} fullWidth required />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField className={classes.field} label="Prenom" name="prenom" value={membre.prenom} onChange={handleChange} fullWidth required />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                className={classes.field}
                select
                label="Region sanitaire d'origine"
                name="regionSanitaire"
                value={regionSanitaire}
                onChange={handleChange}
                fullWidth
                required
              >
                <MenuItem value="" disabled>
                  Selectionnez une region
                </MenuItem>
                {regions.map(region => (
                  <MenuItem key={region.id} value={region.id}>{region.nom}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                className={classes.field}
                select
                label="District sanitaire d'origine"
                name="districtSanitaire"
                value={districtSanitaire}
                onChange={handleChange}
                fullWidth
                required
                disabled={!regionSanitaire}
              >
                <MenuItem value="" disabled>
                  {regionSanitaire ? 'Selectionnez un district' : 'Choisissez une region d abord'}
                </MenuItem>
                {districts.map(district => (
                  <MenuItem key={district.id} value={district.id}>{district.nom}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                className={classes.field}
                select
                label="Structure d'origine"
                name="idStructure"
                value={membre.idStructure}
                onChange={handleChange}
                fullWidth
                required
                disabled={!districtSanitaire}
              >
                <MenuItem value="" disabled>
                  {districtSanitaire ? 'Selectionnez une structure' : 'Choisissez un district d abord'}
                </MenuItem>
                {formations.map(formation => (
                  <MenuItem key={formation.id} value={formation.id}>{formation.nom}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} className={classes.submitRow}>
              <Button type="submit" variant="contained" color="primary" className={classes.submit} disabled={soumissionEnCours}>
                {soumissionEnCours ? 'Soumission...' : 'Soumettre'}
              </Button>
            </Grid>

            {messageSucces && (
              <Grid item xs={12}>
                <div className={`${classes.feedback} ${classes.success}`}>{messageSucces}</div>
              </Grid>
            )}

            {messageErreur && (
              <Grid item xs={12}>
                <div className={`${classes.feedback} ${classes.error}`}>{messageErreur}</div>
              </Grid>
            )}
          </Grid>
        </form>
      </Paper>
      </div>
    </div>
  );
});

export default InscriptionContainer;